import { ErrorBanner } from "components/Error/ErrorBanner";
import { Formik } from "formik";
import type {
  DeviceAssetOut,
  DeviceAssetUpdateIn,
  GatewayAssetUpdateIn,
  GenericCSVDeviceConfig,
  GenericCSVFieldMapping,
} from "kubb";
import { type DeviceConfigurationOutput, mBusAddressingModeEnum } from "kubb";
import { isNumber } from "lodash-es";
import type React from "react";
import { useState } from "react";
import * as Yup from "yup";
import SuccessBanner from "../../../../../components/Banner/SuccessBanner";
import BlockSpinner from "../../../../../components/Spinners/BlockSpinner";
import { Button } from "../../../../../components/Theme/button";
import { Heading } from "../../../../../components/Theme/heading";
import { useUpdateAsset } from "../../../../../hooks/updateEntity";
import { objectBlankStringsToUndefined } from "../../../../../utils/object";
import MBusConfigurationSubForm, {
  MBusConfigurationFormSchema,
} from "./MbusConfigurationForm";
import VirtualMeterConfigurationForm, {
  VirtualMeterConfigurationFormSchema,
} from "./VirtualMeterConfigurationForm.tsx";

function isGenericCsvAsset(asset: DeviceAssetOut): boolean {
  const config = asset.configuration as DeviceConfigurationOutput | undefined;
  if (config?.generic_csv) return true;
  const metadata = asset.amr_metadata as { type?: string } | undefined;
  return metadata?.type === "generic_csv_v1";
}

function GenericCSVConfigurationDisplay(props: {
  asset: DeviceAssetOut;
}): React.ReactElement {
  const config = props.asset.configuration as
    | DeviceConfigurationOutput
    | undefined;
  const genericCsv = config?.generic_csv as GenericCSVDeviceConfig | undefined;
  const fields = genericCsv?.fields ?? [];

  return (
    <>
      <Heading>Generic CSV Field Mappings</Heading>
      {fields.length === 0 ? (
        <p className="text-sm text-zinc-400 italic">
          No field mappings configured.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm divide-y divide-zinc-200 dark:divide-zinc-700">
            <thead>
              <tr className="text-left text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
                <th className="px-3 py-2">Standardised Column</th>
                <th className="px-3 py-2">Identifier Value</th>
                <th className="px-3 py-2">Units</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
              {fields.map((f: GenericCSVFieldMapping) => (
                <tr key={f.identifier_value}>
                  <td className="px-3 py-2 font-medium">
                    {f.standardised_column}
                  </td>
                  <td className="px-3 py-2" style={{ fontFamily: "monospace" }}>
                    {f.identifier_value}
                  </td>
                  <td className="px-3 py-2">{f.units}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

export default function DeviceConfigurationTab(props: {
  asset: DeviceAssetOut;
}): React.ReactElement {
  const configuration: DeviceConfigurationOutput | undefined = props.asset
    .configuration as DeviceConfigurationOutput | undefined;

  const [updated, setUpdated] = useState(false);

  const { update, loading, error } = useUpdateAsset(
    props.asset as DeviceAssetUpdateIn | GatewayAssetUpdateIn,
  );

  const isVirtual = props.asset.virtual_asset;
  const isGenericCsv = isGenericCsvAsset(props.asset);

  if (isGenericCsv) {
    return <GenericCSVConfigurationDisplay asset={props.asset} />;
  }

  const initialValues: DeviceConfigurationOutput = {
    mbus: !isVirtual
      ? {
          primary_address: isNumber(configuration?.mbus?.primary_address)
            ? configuration?.mbus?.primary_address
            : undefined,
          addressing_mode:
            configuration?.mbus?.addressing_mode ||
            mBusAddressingModeEnum.secondary,
          encryption_key: configuration?.mbus?.encryption_key || "",
          baud_rate: configuration?.mbus?.baud_rate || 2400,
        }
      : null,
    virtual: isVirtual
      ? {
          field: configuration?.virtual?.field,
        }
      : null,
  };

  function submit(values: DeviceConfigurationOutput) {
    setUpdated(false);
    update({
      ...props.asset,
      configuration: {
        ...values,
        mbus: objectBlankStringsToUndefined(values.mbus),
      },
    }).then(() => setUpdated(true));
  }

  return (
    <Formik
      onSubmit={submit}
      initialValues={initialValues}
      validationSchema={Yup.object().shape(
        isVirtual
          ? {
              virtual: VirtualMeterConfigurationFormSchema,
            }
          : {
              mbus: MBusConfigurationFormSchema,
            },
      )}
      validateOnMount={true}
      enableReinitialize={true}
    >
      {({ submitForm, isValid, values }): React.ReactElement => {
        return (
          <BlockSpinner loading={loading}>
            {isVirtual ? (
              <VirtualMeterConfigurationForm namespace={"virtual"} />
            ) : (
              <MBusConfigurationSubForm
                addressing_mode={values?.mbus?.addressing_mode}
                namespace={"mbus"}
              />
            )}
            <Button
              type={"submit"}
              color={"brandLight"}
              onClick={submitForm}
              disabled={loading || !isValid}
            >
              Update
            </Button>
            {updated && (
              <SuccessBanner>
                Configuration was updated successfully.
              </SuccessBanner>
            )}
            <ErrorBanner error={error} />
          </BlockSpinner>
        );
      }}
    </Formik>
  );
}
